import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Sparkles, X } from 'lucide-react';

import bannerConfig from '@/data/banners.json';
import { useShopPath } from '@/hooks/useShopPath';
import { useTrialStatus } from '@/lib/shop-status';
import { cn } from '@/lib/utils';

/**
 * Top-of-page banner driven by `data/banners.json`. The JSON declares a
 * `condition` string evaluated against the current trial status. We keep the
 * conditions intentionally narrow (equality checks on the named state) so we
 * don't need a real expression parser. The thresholds mirror the Lovable
 * design's payment-nudge stages (`deriveNudgeStage`: soft 6–19 days left,
 * hard ≤5) so this banner and the Billing page's trial card escalate at the
 * same moment; `daysLeft > 0` doubles as the "still trialing" guard because
 * `useTrialStatus()` reports 0 when not trialing.
 *
 * Trial state comes from `useTrialStatus()`, which reads live Stripe billing
 * via `useBillingStatus()` (NEA-5710). It returns `null` while loading or when
 * the shop has no subscription; in that case the banner renders nothing — we
 * deliberately don't fake trial framing, because guessing wrong is worse than
 * showing no banner at all.
 *
 * Each banner can carry an optional `icon` (lucide name), a `leadingPill`
 * (attention-grabbing pill before the message — used for "{daysLeft} days
 * left") and a `cta.tone` ("primary" or "dark") so renderings can match the
 * Lovable hand-coded variants without us hard-coding them in component code.
 */
const ICONS = { Sparkles };

const VARIANT_CONTAINER = {
	info: 'bg-info-soft text-info-soft-foreground border-primary/10',
	success: 'bg-success-soft text-success-soft-foreground border-success/30',
	warning: 'bg-action-soft text-action-soft-foreground border-action/20',
	destructive: 'bg-destructive text-destructive-foreground border-destructive'
};

function evaluate(condition, ctx) {
	try {
		// eslint-disable-next-line no-new-func
		return Function(
			'day',
			'daysLeft',
			'trialDays',
			'hasPayment',
			`return (${condition});`
		)(ctx.day, ctx.daysLeft, ctx.trialDays, ctx.hasPayment);
	} catch {
		return false;
	}
}

function interpolate(message, vars) {
	return String(message ?? '').replace(/\{(\w+)\}/g, (_, k) => (vars[k] !== undefined && vars[k] !== null ? String(vars[k]) : ''));
}

export function LifecycleBanner() {
	const trial = useTrialStatus();
	const navigate = useNavigate();
	const { shopPath } = useShopPath();
	const [dismissed, setDismissed] = useState({});

	const ctx = trial
		? {
				day: trial.daysIntoTrial,
				daysLeft: trial.daysLeft,
				trialDays: trial.trialDays ?? 30,
				hasPayment: trial.hasPayment
			}
		: null;

	const active = useMemo(() => {
		if (!ctx) return null;
		return bannerConfig.banners.find((b) => evaluate(b.condition, ctx) && !dismissed[b.id]);
	}, [ctx, dismissed]);

	if (!active) return null;

	const vars = { ...ctx, daysWord: ctx.daysLeft === 1 ? 'day' : 'days' };

	const onCta = () => {
		const action = active.cta?.action;
		if (!action) return;
		if (action.startsWith('navigate:')) navigate(shopPath(action.slice('navigate:'.length)));
		else if (action.startsWith('modal:') || action.startsWith('tour:')) window.dispatchEvent(new CustomEvent(action));
	};

	const Icon = active.icon ? ICONS[active.icon] : null;
	const message = interpolate(active.message, vars);
	const leadingPill = active.leadingPill ? interpolate(active.leadingPill, vars) : null;
	const ctaTone = active.cta?.tone === 'dark' ? 'dark' : 'primary';

	// The "hard" trial-ending banner (variant: warning) pins to the top of the
	// viewport while the user scrolls — matches Lovable's hard-stage variant
	// (`sticky top-0 z-40` + soft drop shadow). Soft / info banners aren't
	// sticky: they're a nudge, not a stop-everything message.
	const isSticky = active.variant === 'warning';
	const containerClass = cn(
		'w-full border-b',
		VARIANT_CONTAINER[active.variant] || VARIANT_CONTAINER.info,
		isSticky && 'sticky top-0 z-40 shadow-[0_4px_12px_-6px_rgba(15,23,42,0.18)]'
	);

	return (
		<div className={containerClass}>
			<div className="mx-auto flex max-w-[1100px] items-center gap-3 px-6 py-2.5 md:px-10">
				{leadingPill ? (
					<span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-action px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-action-foreground">
						{leadingPill}
					</span>
				) : Icon ? (
					<Icon className="h-4 w-4 shrink-0 text-primary" />
				) : null}

				<span className="text-sm font-medium">{message}</span>

				<div className="ml-auto flex items-center gap-2">
					{active.cta ? (
						active.cta.action?.startsWith('navigate:') && ctaTone === 'primary' ? (
							<Link
								to={shopPath(active.cta.action.slice('navigate:'.length))}
								className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
							>
								{active.cta.label} <ArrowRight className="h-3.5 w-3.5" />
							</Link>
						) : (
							<button
								type="button"
								onClick={onCta}
								className={
									ctaTone === 'dark'
										? 'inline-flex items-center gap-1.5 rounded-full bg-foreground px-4 py-2 text-sm font-semibold text-background transition hover:opacity-90'
										: 'inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline'
								}
							>
								{active.cta.label}
								{ctaTone === 'dark' ? <ArrowRight className="h-3.5 w-3.5" /> : null}
							</button>
						)
					) : null}
					{active.dismissible ? (
						<button
							type="button"
							onClick={() => setDismissed((d) => ({ ...d, [active.id]: true }))}
							aria-label="Dismiss banner"
							className="rounded-md p-1 text-muted-foreground transition hover:text-foreground"
						>
							<X className="h-4 w-4" />
						</button>
					) : null}
				</div>
			</div>
		</div>
	);
}
